import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input, Select } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import {
  addFoodTemperatureControl,
  coolersTemperatures,
  fishCoolerTemperatures,
  FoodControlPointInstanceDto,
  FoodManagementControls,
  FoodTemperatureControl,
  freezerTemperatures,
  InstanceStatus,
  parseRemarkAndCorrectiveActions,
  Tempature,
  updateFoodTemperatureControl,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface InventoryControlsModalProps extends ModalProps {
  foodControlPointInstance: FoodControlPointInstanceDto;
  type: FoodManagementControls;
  title?: string;
  initialData?: FoodTemperatureControl;
}

export function InventoryControlsModal({
  foodControlPointInstance,
  title,
  type,
  initialData,
  ...props
}: InventoryControlsModalProps) {
  const { t } = useTranslation();
  const [isCorrectiveActionNeeded, setIsCorrectiveActionNeeded] =
    useState<boolean>(false);
  const [selectedTemperature, setSelectedTemperature] = useState<number | null>(
    null,
  );

  const [isRemarksNeeded, setIsRemarksNeeded] = useState<boolean>(false);
  const [note, setNote] = useState<string[]>([]);
  const numberOfCorrectiveActionSelected = useRef(0);
  const addProductionControlSchema = z.object({
    temperature: z
      .boolean()
      .optional()
      .refine((e) => !!selectedTemperature, {
        message: t("food.Temperature is Required"),
      }),
    remarks: z
      .string()
      .optional()
      .refine((e) => !isRemarksNeeded || !isCorrectiveActionNeeded, {
        message: t("food.Remarks are mandatory"),
      }),
    correctiveAction: z
      .boolean()
      .optional()
      .refine(
        (e) =>
          isCorrectiveActionNeeded
            ? !!numberOfCorrectiveActionSelected.current &&
              isCorrectiveActionNeeded
            : true,
        {
          message: t("food.At leat one corrective action needed"),
        },
      ),
  });
  type AddProductionControlData = z.infer<typeof addProductionControlSchema>;

  const queryClient = useQueryClient();

  const getTemperature = (type: FoodManagementControls): Tempature[] => {
    switch (type) {
      case FoodManagementControls.freezer:
        return freezerTemperatures;
      case FoodManagementControls.cooler:
        return coolersTemperatures;
      case FoodManagementControls.fishCooler:
        return fishCoolerTemperatures;
      default:
        return freezerTemperatures;
    }
  };

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<AddProductionControlData>({
    resolver: zodResolver(addProductionControlSchema),
  });

  const updateMutation = useMutation({
    mutationFn: updateFoodTemperatureControl,
    onSuccess: () => {
      setIsCorrectiveActionNeeded(false);
      setIsRemarksNeeded(false);
      setSelectedTemperature(null);
      numberOfCorrectiveActionSelected.current = 0;
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      reset();
      props.onClose?.();
      toast.success(t("food.Inventory Control Has Been Added"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      toast.error(t("food.An error occurred while adding Inventory Control"));
    },
  });

  const mutation = useMutation({
    mutationFn: addFoodTemperatureControl,
    onSuccess: () => {
      setIsCorrectiveActionNeeded(false);
      setIsRemarksNeeded(false);
      setSelectedTemperature(null);
      numberOfCorrectiveActionSelected.current = 0;
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      reset();
      props.onClose?.();
      toast.success(t("food.Inventory Control Has Been Added"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      toast.error(t("food.An error occurred while adding Inventory Control"));
    },
  });

  const noteTexts = {
    note1: t("food.Adjust Effect"),
    note2: t("food.Goods Assessed"),
    note3: t("food.Move cooling unite"),
    note4: t("food.The goods are discarded"),
    note5: t("food.Technician has been called"),
    note6: t("food.Inventory Other"),
  };

  const editNote = (e: React.ChangeEvent<HTMLInputElement>) => {
    const noteText = noteTexts[e.target.name as keyof typeof noteTexts];
    const targetName = e.target.name;
    const isChecked = e.target.checked;
    isChecked
      ? ++numberOfCorrectiveActionSelected.current
      : --numberOfCorrectiveActionSelected.current;
    isChecked
      ? setNote((prev) => [...prev, noteText])
      : setNote((prev) => prev.filter((note) => note !== noteText));
    if (targetName === "note2" || targetName === "note6") {
      setIsRemarksNeeded(isChecked);
    }
  };
  const handleTemperatureValue = (temp: number) => {
    setSelectedTemperature(temp);
    setIsCorrectiveActionNeeded(
      getTemperature(type).find((temperature) => +temperature.value === temp)
        ?.color === "red",
    );
  };

  useEffect(() => {
    if (initialData) {
      const { remark, reason } = parseRemarkAndCorrectiveActions(
        initialData.note,
      );
      setSelectedTemperature(initialData.temperature);
      setNote(reason);
      setIsCorrectiveActionNeeded(
        getTemperature(type).find(
          (temperature) => +temperature.value === initialData.temperature,
        )?.color === "red",
      );

      reset({
        remarks: remark,
      });
    }
  }, [initialData, reset, type]);

  const onSubmit = (data: AddProductionControlData) => {
    const isCompeletedWithErrors = isCorrectiveActionNeeded;

    const payload = {
      companyId: foodControlPointInstance?.companyId,
      companyLocationId: foodControlPointInstance?.companyLocationId,
      reason: JSON.stringify(note),
      remark: data.remarks,
      temperature: selectedTemperature as number,
      status: isCompeletedWithErrors
        ? InstanceStatus.completedWithErrors
        : InstanceStatus.completed,
      controlPointInstanceId: foodControlPointInstance?.id,
      foodControlPointInstance,
    };

    if (initialData) {
      updateMutation.mutate(payload);
      return;
    }

    mutation.mutate(payload);
  };

  return (
    <Modal
      actionButtons={
        <>
          <Button variant="secondary" onClick={props?.onClose}>
            {t("common.Discard")}
          </Button>
          <Button loading={mutation.isPending} onClick={handleSubmit(onSubmit)}>
            {t("food.Submit Entry")}
          </Button>
        </>
      }
      {...props}
      title={title}
    >
      <form>
        <Label required title={t("food.Temperature")}>
          <div className="flex flex-col gap-y-1 w-full items-end">
            <Select // To apply enos colors(blue&rose) we need to access className
              size="md"
              variant="standart"
              placeholder={t("food.Select Temperature")}
              options={getTemperature(type)}
              onChange={(e) => handleTemperatureValue(Number(e.target.value))}
              defaultValue={initialData?.temperature?.toString()}
            />
            <ErrorMessage message={errors?.temperature?.message} />
          </div>
        </Label>

        {isCorrectiveActionNeeded && (
          <>
            <div className="border-b border-b-gray-200 my-2.5"></div>
            <Label required title={t("food.Corrective Action")}>
              <div className="flex flex-col w-full gap-2 ">
                <div className="flex flex-row gap-4 items-center">
                  <input
                    name="note1"
                    className="checkbox checkbox-md"
                    type="checkbox"
                    onChange={(e) => editNote(e)}
                    defaultChecked={note.includes(noteTexts.note1)}
                  />
                  {noteTexts.note1}
                </div>
                <div className="flex flex-row gap-4 items-center">
                  <input
                    onChange={(e) => editNote(e)}
                    className="checkbox checkbox-md"
                    type="checkbox"
                    defaultChecked={note.includes(noteTexts.note2)}
                    name="note2"
                  />
                  {noteTexts.note2}
                </div>
                <div className="flex flex-row gap-4 items-center">
                  <input
                    name="note3"
                    className="checkbox checkbox-md"
                    type="checkbox"
                    defaultChecked={note.includes(noteTexts.note3)}
                    onChange={(e) => editNote(e)}
                  />
                  {noteTexts.note3}
                </div>
                <div className="flex flex-row gap-4 items-center">
                  <input
                    name="note4"
                    className="checkbox checkbox-md"
                    type="checkbox"
                    defaultChecked={note.includes(noteTexts.note4)}
                    onChange={(e) => editNote(e)}
                  />
                  {noteTexts.note4}
                </div>
                <div className="flex flex-row gap-4 items-center">
                  <input
                    name="note5"
                    className="checkbox checkbox-md"
                    type="checkbox"
                    defaultChecked={note.includes(noteTexts.note5)}
                    onChange={(e) => editNote(e)}
                  />
                  {noteTexts.note5}
                </div>
                <div className="flex flex-row gap-4 items-center">
                  <input
                    name="note6"
                    className="checkbox checkbox-md"
                    type="checkbox"
                    defaultChecked={note.includes(noteTexts.note6)}
                    onChange={(e) => editNote(e)}
                  />
                  {noteTexts.note6}
                </div>
                <ErrorMessage message={errors?.correctiveAction?.message} />
              </div>
            </Label>
          </>
        )}
        <div className="border-b border-b-gray-200 my-2.5"></div>
        <Label title={t("food.Remarks")}>
          <div className="flex flex-col items-end w-full gap-2 ">
            <Input
              {...register("remarks")}
              className={
                isRemarksNeeded ? `text-[#8D4F61] border border-[#8D4F61]` : ""
              }
              placeholder="-"
              onChange={(e) => {
                setIsRemarksNeeded(!e.target.value);
              }}
            />
            <ErrorMessage message={errors?.remarks?.message} />
          </div>
        </Label>
      </form>
    </Modal>
  );
}
